import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Phone, Mail, MapPin, Users, Shield, Clock, Award } from 'lucide-react';
import pieterPhoto from '@/assets/pieter-photo.jpg';
import pieterOfficePhoto from '@/assets/pieter-office-photo.png';
import pieterMeetingPhoto from '@/assets/pieter-meeting-room.jpg';
import andreaPhoto from '@/assets/andrea-photo.png';
import { openCalendlyPopup } from '@/utils/calendly';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import Autoplay from "embla-carousel-autoplay";
const OverOns = () => {
  return <div className="min-h-screen">
      <Helmet>
        <title>Over Compleet Verzekerd - Persoonlijk Verzekeringsadvies</title>
        <meta name="description" content="Over Compleet Verzekerd: Pieter van Heusden en Andrea de Ridder. Persoonlijk advies, transparantie en expertise. Alle verzekeringen onder één dak." />
        <meta name="keywords" content="over compleet verzekerd, pieter van heusden, andrea de ridder, verzekeringsadviseurs, persoonlijk advies, transparantie, expertise verzekeringen" />
        <link rel="canonical" href="https://compleetverzekerd.nl/over-ons/" />
        <script type="application/ld+json">
          {`{
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Pieter van Heusden",
            "jobTitle": "Adviseur Verzekeringen",
            "worksFor": {
              "@type": "LocalBusiness",
              "name": "Compleet Verzekerd"
            },
            "email": "p.vanheusden@compleetverzekerd.nl",
            "telephone": "+31782200021"
          }`}
        </script>
      </Helmet>
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary/10 to-accent/10 py-16 md:py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
                Over Compleet Verzekerd
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Persoonlijk, transparant en deskundig verzekeringsadvies. 
                Alle verzekeringen onder één dak.
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                  Wie zijn wij?
                </h2>
              </div>
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 mb-12">
                <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                  Bij Compleet Verzekerd adviseren en bemiddelen we in allerlei soorten verzekeringen. 
                  De naam zegt het eigenlijk al: je bent compleet verzekerd. Van schadeverzekeringen 
                  tot werknemersverzekeringen. Van inkomensverzekeringen tot aansprakelijkheidsverzekeringen.
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Bij Compleet Verzekerd verzeker je alles onder één dak. Makkelijk en transparant.
                </p>
              </div>
              
              <div className="text-center mb-12">
                <div className="mb-8 max-w-[600px] mx-auto">
                  <Carousel className="w-full" plugins={[Autoplay({
                  delay: 4000
                })]} opts={{
                  align: "start",
                  loop: true
                }}>
                    <CarouselContent>
                      <CarouselItem>
                        <img src={pieterOfficePhoto} alt="Pieter van Heusden op kantoor - Persoonlijke benadering" className="w-full h-80 rounded-2xl object-cover border-4 border-primary/20 shadow-lg" />
                      </CarouselItem>
                      <CarouselItem>
                        <img src={pieterMeetingPhoto} alt="Pieter van Heusden in gesprek met klant - Persoonlijke benadering" className="w-full h-80 rounded-2xl object-cover border-4 border-primary/20 shadow-lg" />
                      </CarouselItem>
                    </CarouselContent>
                    <CarouselPrevious />
                    <CarouselNext />
                  </Carousel>
                </div>
                <h3 className="text-2xl font-semibold text-foreground mb-4">Persoonlijke benadering</h3>
                <p className="text-muted-foreground text-lg leading-relaxed max-w-3xl mx-auto">Wij geloven in persoonlijk contact en een warme, professionele benadering. Bij ons ben je geen nummer, maar een mens met unieke wensen en behoeften.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ons team
              </h2>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8">
                <Card className="border-border hover:shadow-lg transition-shadow">
                  <CardContent className="p-8 text-center">
                    <div className="mb-6">
                      <img src={pieterPhoto} alt="Pieter van Heusden - Adviseur Verzekeringen" className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-primary/20" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">Pieter van Heusden</h3>
                    <p className="text-primary font-medium mb-4">Adviseur Verzekeringen</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        <a href="tel:0782200021" className="text-primary hover:underline">
                          078 - 220 00 21
                        </a>
                      </div>
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        <span className="text-primary">
                          p.vanheusden@compleetverzekerd.nl
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border hover:shadow-lg transition-shadow">
                  <CardContent className="p-8 text-center">
                    <div className="mb-6">
                      <img src={andreaPhoto} alt="Andrea de Ridder - Adviseur Verzekeringen" className="w-32 h-32 rounded-full mx-auto object-cover border-4 border-primary/20" />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">Andrea de Ridder</h3>
                    <p className="text-primary font-medium mb-4">Adviseur Verzekeringen</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        <a href="tel:0782200021" className="text-primary hover:underline">
                          078 - 220 00 21
                        </a>
                      </div>
                      <div className="flex items-center justify-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        <span className="text-primary">
                          a.deridder@compleetverzekerd.nl
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Ons adviesproces
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Van eerste contact tot nazorg. Wij begeleiden je door het hele proces.</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[{
              step: "1",
              title: "Wensen",
              description: "In een persoonlijk adviesgesprek gaan we in op jouw wensen, eisen, risico's en persoonlijke situatie."
            }, {
              step: "2",
              title: "Advies",
              description: "Na een analyse van je situatie zoeken wij het beste aanbod voor jouw situatie. We bespreken alle mogelijkheden uitgebreid met je."
            }, {
              step: "3",
              title: "Beslissing",
              description: "Neem de tijd voor een weloverwogen beslissing. Heb je nog vragen? Neem vooral contact met ons op."
            }, {
              step: "4",
              title: "Service",
              description: "Na afsluiting blijven wij jouw aanspreekpunt voor vragen, wijzigingen en schadeafhandeling."
            }].map((item, index) => <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </div>)}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 bg-muted/50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Onze waarden
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="border-border hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <Clock className="h-16 w-16 text-primary mx-auto mb-6" />
                  <h3 className="text-xl font-semibold text-foreground mb-4">Persoonlijke aandacht</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    We nemen de tijd voor u. Geen haast, geen druk, 
                    maar rustig uitleggen en adviseren.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <Shield className="h-16 w-16 text-primary mx-auto mb-6" />
                  <h3 className="text-xl font-semibold text-foreground mb-4">Transparantie</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Geen verborgen kosten of onduidelijkheden. 
                    Wij leggen alles helder en begrijpelijk uit.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-border hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <Award className="h-16 w-16 text-primary mx-auto mb-6" />
                  <h3 className="text-xl font-semibold text-foreground mb-4">Expertise</h3>
                  <p className="text-muted-foreground leading-relaxed">
                    Jarenlange ervaring in de verzekeringsbranche 
                    en up-to-date kennis van de markt.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-2xl p-8 text-center">
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Onze missie</h2>
                <p className="text-muted-foreground text-lg leading-relaxed mb-4">
                  Bij Compleet Verzekerd zijn we van mening dat je alleen risico's moet verzekeren 
                  die je financieel niet zelf kunt dragen. Wij ontzorgen particuliere en zakelijke 
                  klanten met deskundig verzekeringsadvies, keuze voor de juiste verzekeringen, 
                  goede voorwaarden, scherpe premies en persoonlijke service!
                </p>
                <p className="text-muted-foreground text-lg leading-relaxed">Voor ons het allerbelangrijkst: Je ontvangt van ons een advies dat vanuit het hoofd en het hart wordt gemaakt. We kijken niet alleen naar de kosten, maar vooral naar jouw persoonlijke situatie, wensen en eisen.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-6 text-center">
            <Users className="w-16 h-16 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Neem contact met ons op
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Klaar voor persoonlijk en deskundig verzekeringsadvies?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <a href="tel:0782200021" className="hover:underline">
                  078 - 220 00 21
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <span>
                  info@compleetverzekerd.nl
                </span>
              </div>
            </div>
            <Button size="lg" variant="secondary" className="bg-background text-foreground hover:bg-background/90" onClick={openCalendlyPopup}>
              Maak direct een afspraak
            </Button>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>;
};
export default OverOns;